import { useTranslation } from 'react-i18next';
import { Flex, Typography } from 'antd';
import classNames from 'classnames';
import { useGetClientInsuranceLink } from 'api/clients/use-get-client-insurance-link';
import { useDownloadFile } from 'shared/hooks/use-download-file';
import { formatDate } from 'shared/utils/time/format-date';
import { ClientInsuranceHistory } from 'components/clients/client-insurance/components/client-insurance-history';
import { ClientInsurancePreview } from 'components/clients/client-insurance/components/client-insurance-preview';
import { EditClientInsuranceDates } from 'components/clients/client-insurance/components/edit-client-insurance-dates';
import { UploadClientInsurance } from 'components/clients/client-insurance/components/upload-client-insurance';
import styles from './client-insurance-details.module.scss';
import { INSURANCE_STATUSES_REQUIRING_ATTENTION } from './constants';
import type { IClientInsuranceDetailsProps } from './types';

export const ClientInsuranceDetails = (props: IClientInsuranceDetailsProps) => {
  const { clientId, insurance } = props;
  const { t } = useTranslation('client');
  const downloadFile = useDownloadFile();
  const { mutateAsync: getInsuranceLink } = useGetClientInsuranceLink();

  const requiresAttention = INSURANCE_STATUSES_REQUIRING_ATTENTION.includes(insurance.status);

  const isExpired = insurance.status === 'expired';

  const handleDownload = async () => {
    const result = await getInsuranceLink({ clientId, insuranceId: insurance.id });
    downloadFile(result.url);
  };

  return (
    <div className={styles.details}>
      <Flex className={styles.file} align='center' gap={12} justify='space-between'>
        <Flex className={styles.fileInfo} vertical>
          <Typography.Link className={styles.fileName} ellipsis onClick={handleDownload}>
            {insurance.fileName}
          </Typography.Link>
          <Typography.Text className={styles.uploadedAt}>
            {t('insurance_uploaded_at', { date: formatDate(insurance.createdAt) })}
          </Typography.Text>
        </Flex>
        <ClientInsurancePreview
          clientId={clientId}
          insuranceId={insurance.id}
          fileName={insurance.fileName}
          mimeType={insurance.mimeType}
        />
      </Flex>

      <div className={styles.dates}>
        <Flex vertical>
          <Typography.Text className={styles.dateLabel}>{t('insurance_valid_from')}</Typography.Text>
          <Typography.Text className={styles.dateValue}>{insurance.validFrom}</Typography.Text>
        </Flex>
        <Flex vertical>
          <Typography.Text className={styles.dateLabel}>{t('insurance_valid_to')}</Typography.Text>
          <Typography.Text
            className={classNames(styles.dateValue, {
              [styles.dateExpired]: isExpired,
              [styles.dateWarning]: requiresAttention && !isExpired,
            })}>
            {insurance.validTo}
          </Typography.Text>
        </Flex>
      </div>

      <div className={styles.actions}>
        <EditClientInsuranceDates clientId={clientId} insurance={insurance} />
        <UploadClientInsurance
          clientId={clientId}
          buttonLabel={t('insurance_upload_new_action')}
          buttonProps={{ type: requiresAttention ? 'primary' : 'default' }}
        />
      </div>

      <ClientInsuranceHistory clientId={clientId} />
    </div>
  );
};
